import {createStyles, Switch} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import AntibodyEditModal from 'antibodies/actions/AntibodyEditModal';
import useAntibodyMapState from 'antibodies/antibody-map/useAntibodyMapState';
import {AntibodyWithVersion} from 'antibodies/AntibodyTypedefs';
import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {useAntibodyVersionQuery} from 'antibodies/hooks/useAntibodyVersionQuery';
import BufferOptionsItem from 'buffers/BufferOptionsItem';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';

const useStyles = createStyles(() => ({
  switchBody: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    marginBottom: 0, // Override benchling.css
  },
  switchLabel: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    ...FONT_PROXIMA_13,
  },
}));

export interface AntibodyMapTabOptionsProps {
  antibodyId: string;
  isEditable: boolean;
}

export default function AntibodyMapTabOptions({
  antibodyId,
  isEditable,
}: AntibodyMapTabOptionsProps): JSX.Element {
  const {classes} = useStyles();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    state: {showLabels},
    setShowLabels,
  } = useAntibodyMapState(antibodyId);
  const {
    state: {openVersionId},
  } = useAntibodyUIState(antibodyId);
  const {antibody} = useAntibodyVersionQuery(antibodyId, openVersionId);

  function openEditModal(): void {
    // antibody and its version are guaranteed to be non-null because the edit button is only enabled when non-null
    runModal(AntibodyEditModal, {antibody: antibody as AntibodyWithVersion});
  }

  return (
    <div className={classNames('buffer-options-container', css(styles.wrapper))}>
      {/* TODO: Should we use VisibilityOptions? Should we introduce a VisibilityOptions-like but fully React/Redux system? */}
      <BufferOptionsItem className={css(styles.visibilityOptionsWrapper)}>
        <Dropdown
          closeOnInsideClick={false}
          isOpen={dropdownOpen}
          onToggle={setDropdownOpen}
          menu={
            <Menu
              dropLeft={true}
              // We're not selecting items per se, so don't track state for this menu
              value={[]}
              isMulti={true}
              scrollable={true}
              height={MenuHeight.MEDIUM}
              width={MenuWidth.LARGE}
            >
              {/* TODO: Make this data driven instead of hard coded. (i.e. hard-code a list of OptionData, lol) */}
              <Option
                skipIconOverride={true}
                onSelect={() => {
                  setShowLabels(!showLabels);
                }}
              >
                <Switch
                  checked={showLabels}
                  classNames={{body: classes.switchBody, label: classes.switchLabel}}
                  onChange={(e) => {
                    setShowLabels(!e.currentTarget.checked);
                  }}
                  size="sm"
                  labelPosition="left"
                  label="Labels"
                />
              </Option>
            </Menu>
          }
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            icon="settings"
            isDropdown={true}
            size={ButtonSize.MINI}
          />
        </Dropdown>
      </BufferOptionsItem>
      <Button
        text="Edit protein"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon="edit"
        size={ButtonSize.MINI}
        onClick={openEditModal}
        disabled={!isEditable || antibody == null || antibody.currentVersionId !== openVersionId}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  visibilityOptionsWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});
